/**
 * Single source of truth for the `arguments` object sent with Client AI streaming POSTs
 * (BedrockConverse / useChat). Keeps admin testing tabs and embedded apps aligned.
 */
/**
 * Strips placeholder-only customer context so it matches "no host page" (undefined),
 * which the backend treats as no customer-context snippet in the system prompt.
 *
 * Prompt Testing previously sent `{ isLoggedIn: false }` alone, which is truthy and caused
 * a different system prompt than Shopping Assistant (undefined context in admin).
 */
export function normalizeCustomerContextForClientAiRequest(customerContext) {
    if (customerContext == null) {
        return undefined;
    }
    if (typeof customerContext !== 'object' || Array.isArray(customerContext)) {
        return customerContext;
    }
    const o = customerContext;
    const hasPage = o.currentPage != null && typeof o.currentPage === 'object';
    const cp = o.currentPage;
    const hasMeaningfulLocale = (typeof o.locale === 'string' && o.locale.trim() !== '')
        || (typeof cp?.locale === 'string' && String(cp.locale).trim() !== '');
    const hasUrl = typeof cp?.url === 'string' && cp.url.trim() !== '';
    if (!hasPage && !hasMeaningfulLocale && !hasUrl) {
        return undefined;
    }
    return customerContext;
}
/**
 * Builds the `arguments` payload for Client AI stream requests (same shape as useChat experimental_prepareRequestBody).
 */
export function buildClientAIRequestArguments(params) {
    const layoutMode = params.conversationOptions.layoutMode || 'ai';
    const isDualSearchMode = Boolean(params.portalClientAIUseDualSearch && layoutMode === 'dual');
    const cc = normalizeCustomerContextForClientAiRequest(params.customerContext);
    const adminOptions = {
        ...(params.adminOptions || {}),
    };
    if (params.postFilter === true) {
        adminOptions.postFilter = true;
    }
    const args = {
        options: params.conversationOptions,
        adminOptions,
        organizationID: params.organizationID,
        portalID: params.portalID,
        isDualSearchMode,
        productType: params.conversationOptions.conversationType,
        sessionID: params.sessionID,
        userID: params.userID,
        location: params.location,
    };
    if (cc !== undefined) {
        args.customerContext = cc;
    }
    if (params.postFilter === true) {
        args.postFilter = true;
    }
    return args;
}
/**
 * Same resolution order as Bedrock `buildSystemPrompt` for shoppingAssistant when no admin override is sent.
 * Lets callers pass explicit `systemPromptOverride` in adminOptions to match production widgets.
 */
export function getShoppingAssistantSystemPromptOverride(portal, fallbackPrompt) {
    const p = portal;
    return (p.clientAIConfig?.shoppingAssistantPrompt
        || p.clientAIPromptTemplate
        || fallbackPrompt);
}
//# sourceMappingURL=buildClientAIRequestArguments.js.map